import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AtAGlance from '@/components/ui/AtAGlance';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 2290 Instructions | How to File Heavy Vehicle Use Tax"
          description="Learn how to file IRS Form 2290 for Heavy Vehicle Use Tax, including who must file, due dates, and step-by-step instructions to complete your return correctly."
          keywords="form 2290 instructions, form 2290, 2290 online, heavy vehicle use tax, irs form 2290, 2290 tax, form 2290 online"
          canonical="resources/excise-tax/form-2290-instructions/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form 2290</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file your IRS Form 2290 for the <b>2026-2027 tax year</b> with these
          easy instructions.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
          >
            E-File 2290 Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
        component="article"
        itemScope
        itemType="https://schema.org/Article"
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />

          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 2,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ mb: 1, textAlign: 'right' }}
                >
                  <Typography variant="body2">
                    Last updated:{' '}
                    <time
                      itemProp="datePublished"
                      dateTime="2026-08-21T09:00:00-05:00"
                      style={{ display: 'none' }}
                    >
                      August 21, 2026
                    </time>
                    <time
                      itemProp="dateModified"
                      dateTime="2026-08-21T09:00:00-05:00"
                    >
                      August 21, 2026
                    </time>
                  </Typography>

                  <address
                    rel="author"
                    itemProp="author"
                    itemScope
                    itemType="https://schema.org/Person"
                    style={{ fontSize: 14, fontWeight: 600 }}
                  >
                    - by{' '}
                    <span itemProp="name">
                      <Link
                        itemProp="url"
                        href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                        sx={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        Alexia Zepeda
                      </Link>
                    </span>{' '}
                    | TaxZerone
                  </address>
                </Stack>

                <AtAGlance
                  hrefUrl={`${process.env.NEXT_PUBLIC_HOMESITE_URL}resources/excise-tax/form-2290-instructions/`}
                  headerText="Form 2290 at a Glance"
                >
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 2290{' '}
                        </Link>{' '}
                        is used to file the Heavy Vehicle Use Tax (HVUT) return
                        with the IRS for vehicles with a taxable gross weight of{' '}
                        <strong style={{ fontWeight: 600 }}>
                          55,000 pounds or more
                        </strong>{' '}
                        that{' '}
                        <strong style={{ fontWeight: 600 }}>operate</strong> on
                        public highways.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="/resources/excise-tax/form-2290-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          Due Date:{' '}
                        </Link>{' '}
                        HVUT is due by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          last day of the month{' '}
                        </strong>
                        following the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          vehicle's First Used Month (FUM).{' '}
                        </strong>
                        For vehicles first used in{' '}
                        <strong style={{ fontWeight: 600 }}>July,</strong> the
                        annual filing deadline is{' '}
                        <strong style={{ fontWeight: 600 }}>August 31.</strong>
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        After the IRS accepts your return, you'll receive a{' '}
                        <Link
                          href="/resources/excise-tax/form-2290-schedule-1/"
                          sx={{ color: '#0202ff' }}
                        >
                          stamped Schedule 1
                        </Link>
                        , which is required for registration,{' '}
                        <Link
                          href="/resources/excise-tax/irp/"
                          sx={{ color: '#0202ff' }}
                        >
                          IRP
                        </Link>{' '}
                        renewal, or tag renewal with your{' '}
                        <strong style={{ fontWeight: 600 }}>
                          state Department of Motor Vehicles{' '}
                        </strong>
                        (DMV).
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <Link
                          href="/resources/excise-tax/hvut-form-2290-penalties/"
                          sx={{ color: '#0202ff' }}
                        >
                          Penalties:
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          4.5% of late filing penalty{' '}
                        </strong>
                        and a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          0.5% late payment penalty{' '}
                        </strong>
                        on the unpaid tax, plus an interest of{' '}
                        <strong style={{ fontWeight: 600 }}>0.54% </strong>
                        per month.
                      </span>
                    </li>
                  </Box>
                </AtAGlance>

                <Box component="header">
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    What is IRS Form 2290?
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        IRS Form 2290
                      </Link>
                      , Heavy Highway Vehicle Use Tax Return, is used to figure
                      and pay road taxes for heavy highway motor vehicles with a
                      taxable gross weight of 55,000 pounds or more that operate
                      on public highways. This return must also be filed when
                      the acquisition of used vehicles is made for the current
                      tax period.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      However, vehicles that run less than 5,000 miles (7,500
                      miles for agricultural vehicles) are considered
                      <Link
                        href="/resources/excise-tax/form-2290-taxable-suspended-vehicles/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        tax-suspended vehicles
                      </Link>
                      . If you own such a vehicle, you are not required to pay
                      the heavy vehicle tax for it but should file the irs 2290
                      form.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="excise-promo-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: '#f8f9fa',
                          fontSize: 18,
                        }}
                      >
                        Get Your{' '}
                        <Box component="span" sx={{ color: '#e2e00f' }}>
                          IRS-Stamped Schedule 1
                        </Box>{' '}
                        for the 2026–2027 Tax Year
                      </Typography>
                      <Typography variant="body2" color="#f5efeb">
                        E-file your 2290 for the{' '}
                        <span style={{ fontWeight: 600 }}>
                          2026-2027 tax year
                        </span>{' '}
                        and receive your stamped Schedule 1 in minutes.
                      </Typography>

                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { md: 20, sm: 18, xs: 18 },
                          mt: 1.5,
                          color: '#333',
                          lineHeight: 1.5,
                          backgroundColor: '#d8f3dc',
                          border: '2px dashed #20346f',
                          px: 1,
                          mx: 5.8,
                        }}
                      >
                        <span style={{ fontWeight: 600, color: '#177262' }}>
                          {' '}
                          Save 10%
                        </span>{' '}
                        — Use Code{' '}
                        <span
                          style={{ fontWeight: 600, color: '#ff5400' }}
                          // component={'strong'}
                          // sx={{
                          //   ml: 0,
                          //   color: '#c02121',
                          //   fontWeight: 600,
                          //   border: '2px dashed #20346f',
                          //   px: 1,
                          //   backgroundColor: 'rgb(255, 250, 205)',
                          // }}
                        >
                          EFILE2290
                        </span>{' '}
                        at Checkout!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file Form 2290?
                </Typography>

                <Typography variant="body1" sx={{ mb: 5 }}>
                  A trucker owner or a driver who registers in their name of a
                  heavy highway motor vehicle with a taxable gross weight of
                  55,000 pounds or more must complete and{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file form 2290
                  </Link>{' '}
                  irs to figure out and pay HVUT with the IRS every tax year.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 2290 due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    due date to file Form 2290
                  </Link>{' '}
                  with the IRS will be the last day of the month following the
                  vehicle&apos;s First Used Month (FUM). The current tax period
                  for heavy highway vehicles begins on July 1, 2026, and ends on
                  June 30, 2027.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The annual Heavy Vehicle Use Tax Form 2290 filing deadline for
                  this tax year is August 31.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS Due Date Chart for Tax Year 2026:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If, in this period,
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                            the vehicle is first used during
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Then, file IRS Form 2290 and
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                            make your payment by*
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>July 2026</TableCell>
                          <TableCell>August 31, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>August 2026</TableCell>
                          <TableCell>September 30, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>September 2026</TableCell>
                          <TableCell>November 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>October 2026</TableCell>
                          <TableCell>November 30, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>November 2026</TableCell>
                          <TableCell>December 31, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>December 2026</TableCell>
                          <TableCell>February 1, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>January 2027</TableCell>
                          <TableCell>March 1, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>February 2027</TableCell>
                          <TableCell>March 31, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>March 2027</TableCell>
                          <TableCell>April 30, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>April 2027</TableCell>
                          <TableCell>June 1, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>May 2027</TableCell>
                          <TableCell>June 30, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>June 2027</TableCell>
                          <TableCell>August 2, 2027 </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" sx={{ mt: 1, color: '#6f6c7f' }}>
                    *File by this date regardless of when the state registration
                    for the vehicle is due. If any due date falls on a Saturday,
                    Sunday, or legal holiday, file by the next business day.
                  </Typography>
                </Box>
                {/* <Box
                  component={'div'}
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        E-file 2290 quickly with TaxZerone.
                      </Typography>
                      <Typography variant="body2">
                        E-file IRS Form 2290 with TaxZerone and get your
                        Schedule 1 in minutes. Complete the whole filing process
                        by following three simple steps in less than 5 minutes.{' '}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 2290 penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The IRS imposes penalties for failing to file Form 2290 or pay
                  taxes on or before the due date. There are also penalties for
                  filing false or fraudulent returns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Below are the penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Failure to file the Heavy Vehicle Used Tax (HVUT)
                            return by the deadline.
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Failure to file both the 2290 return and heavy
                            vehicle taxes
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            4.5% of the total tax amount due and the penalty
                            will increase monthly for up to five months.
                          </TableCell>
                          <TableCell>
                            0.5% of your total tax amount. You will also face an
                            additional interest charge of 0.54% per month.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  To avoid penalties, you can show a reasonable cause for
                  failing to file Form 2290 or pay taxes on time and can request
                  penalty relief by sending a letter to the IRS explaining the
                  reason.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 2290 Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here are the step-by-step{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    irs form 2290 instructions
                  </Link>{' '}
                  to fill out HVUT(Form 2290)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/2290-form-instructions.webp" />
                  <source srcSet="/assets/images/2290-form-instructions.png" />
                  <Box
                    component="img"
                    src="/assets/images/2290-form-instructions.png"
                    loading="lazy"
                    alt="Form 2290: Enter Name, EIN and complete address "
                    width={760}
                    height={227}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Name</b> - Enter the name of the filer. The name
                      entered should match the name on the IRS database.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>EIN</b> - Enter the Employer Identification Number
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Address</b> - Enter your organization&apos;s address.
                      If the address is outside the United States, in the space
                      for “city or town, state, and ZIP code,” enter the
                      information in the following order: city, province or
                      state, and country.
                    </ListItemText>
                  </ListItem>
                </List>
                <picture>
                  <source srcSet="/assets/images/2290-check-if-applicable.webp" />
                  <source srcSet="/assets/images/2290-check-if-applicable.png" />
                  <Box
                    component="img"
                    src="/assets/images/2290-check-if-applicable.png"
                    loading="lazy"
                    alt="Form 2290: Check if applicable – Address change, VIN correction, Amended return, Final return"
                    width={760}
                    height={162}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Check the below lines if applicable,
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Address Change</b> - If your address has changed, check
                      the Address Change box.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Link
                        href="/excise-tax/efile-irs-form-2290-vin-correction-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        <b>VIN Correction</b>
                      </Link>{' '}
                      - Check the VIN Correction box if you are correcting a VIN
                      listed on a previously filed Schedule 1. List the
                      corrected VIN or VINs on Schedule 1. Also, be sure to use
                      Form 2290 for the tax period you&apos;re correcting.
                      Attach a statement with an explanation for the VIN
                      correction.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Link
                        href="/resources/excise-tax/form-2290-amendments/"
                        sx={{ color: '#0202ff' }}
                      >
                        <b>Amended Return</b>
                      </Link>{' '}
                      - Check the Amended Return box only if you&apos;re
                      reporting an additional tax from an{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-weight-increased-amendment/"
                        sx={{ color: '#0202ff' }}
                      >
                        increase in taxable gross vehicle weight
                      </Link>
                      , or suspended vehicles{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-mileage-exceeded-amendment/"
                        sx={{ color: '#0202ff' }}
                      >
                        exceeding the mileage use limit
                      </Link>
                      .
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Final Return</b> - If you no longer have vehicles to
                      report, check the Final Return box, sign the return, and
                      mail it to the IRS. This will be considered your final
                      2290 return.
                    </ListItemText>
                  </ListItem>
                </List>
                {/* <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        E-file 2290 for suspended vehicles using TaxZerone.
                      </Typography>
                      <Typography variant="body2">
                        If your vehicles meet the low-mileage suspension
                        criteria,{' '}
                        <span style={{ fontWeight: 600 }}>
                          filing Form 2290
                        </span>{' '}
                        (Suspended) with the IRS is mandatory.{' '}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
                      >
                        E-File Now!
                      </Button>
                    </Box>
                  </Stack>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Part I - Figuring the Tax
                </Typography>
                <picture>
                  <source srcSet="/assets/images/2290-part1.webp" />
                  <source srcSet="/assets/images/2290-part1.png" />
                  <Box
                    component="img"
                    src="/assets/images/2290-part1.png"
                    loading="lazy"
                    alt="Form 2290 Part I: Enter the total tax, credit details and balance due."
                    width={760}
                    height={162}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Line 1:</span> Enter the
                  date for the month of first vehicle use during the tax period.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Line 2:</span> To figure the
                  tax amount on line 2, complete the Tax Computation table on
                  page 2. Enter that total in line 2.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Don&apos;t use line 2 to report additional tax
                  from an increase in taxable gross weight.
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Line 3:</span> Report the
                  additional tax from an increase in taxable gross weight.
                  Complete this line only if the taxable gross weight of a
                  vehicle increases during the period and the vehicle falls into
                  a new category.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Line 4:</span> Add lines 2
                  and 3 and enter the total tax amount.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 5:</b> Complete line 5 only if you are claiming a
                  credit for tax paid on a vehicle that was:
                </Typography>
                <List>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Sold before June 1 and not used during the remainder of
                      the period,
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Destroyed (so damaged by an accident or other casualty it
                      isn&apos;t economical to rebuild it) or stolen before June
                      1 and not used during the remainder of the period, or
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Used during the prior period 5,000 miles or less (7,500
                      miles or less for agricultural vehicles)
                    </ListItemText>
                  </ListItem>
                </List>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> The amount claimed on line 5 can&apos;t exceed
                  the tax reported on line 4
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 5,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Line 6:</span>&nbsp;Subtract
                  line 5 from line 4 and enter the amount in line 6. This is the
                  tax amount you owe. Check the respective box on how
                  you&apos;re going to make the payment; either via{' '}
                  <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                    {' '}
                    EFTPS
                  </Link>{' '}
                  or Credit/Debit card.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Part II. Statement in Support of Suspension
                </Typography>
                <picture>
                  <source srcSet="/assets/images/2290-part2.webp" />
                  <source srcSet="/assets/images/2290-part2.png" />
                  <Box
                    component="img"
                    src="/assets/images/2290-part2.png"
                    loading="lazy"
                    alt="Form 2290 Part II: Complete this section to suspend the tax on vehicles expected to be used less than the mileage use limit during a period."
                    width={760}
                    height={211}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 7:</b> Complete this line to suspend the tax on
                  vehicles expected to be used less than the mileage use limit
                  during a period.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  In addition to that, you must also
                </Typography>
                <List>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      List the vehicles on which the tax is suspended on
                      Schedule 1 and
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Count the number of tax-suspended vehicles (designated by
                      category W) listed on Schedule 1, Part I, and enter the
                      number on Schedule 1, Part II, line b.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 8a:</b> Verify in this line that vehicles listed as
                  suspended on Form 2290 for the prior tax period and used 5,000
                  miles or less (7,500 miles or less for agricultural vehicles)
                  were not subject to the tax for that period.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 8b:</b> If you checked box 8a, you must list here the
                  VINs of the vehicles listed as suspended in the prior period
                  and then used for 5,000 miles or more during the period (7,500
                  miles or more for agricultural vehicles).
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 9:</b> Complete this line under the following context
                  - If in the prior period, line 7, was completed and the
                  tax-suspended vehicles were sold or otherwise transferred.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/2290-third-party.webp" />
                  <source srcSet="/assets/images/2290-third-party.png" />
                  <Box
                    component="img"
                    src="/assets/images/2290-third-party.png"
                    loading="lazy"
                    alt="Form 2290: Enter the third-party designee’s name, Phone number and PIN."
                    width={760}
                    height={51}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Third Party Designee:</b> If you want to allow an employee
                  of your business, a return preparer, or other third parties to
                  discuss your Form 2290 with the IRS, check the Yes box in the
                  Third Party Designee section of Form 2290.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Also, enter the designee&apos;s name, phone number, and any
                  five digits that the person chooses as their personal
                  identification number (PIN). The authorization applies only to
                  the tax return on which it appears.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-2290-signature.webp" />
                  <source srcSet="/assets/images/form-2290-signature.png" />
                  <Box
                    component="img"
                    src="/assets/images/form-2290-signature.png"
                    loading="lazy"
                    alt="Form 2290: Enter your signature, date, name and phone number "
                    width={760}
                    height={102}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Signature:</b> After you verify all the information in the
                  return, sign it along with the date and telephone number.
                  Returns filed without a signature will be sent back to you for
                  signing.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/2290-paid-preparer.webp" />
                  <source srcSet="/assets/images/2290-paid-preparer.png" />
                  <Box
                    component="img"
                    src="/assets/images/2290-paid-preparer.png"
                    loading="lazy"
                    alt="Form 2290: Enter the preparer’s name, signature, date, PTIN, Firm’s name, Firm’s address, Firm’s EIN and phone number."
                    width={760}
                    height={67}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Paid Preparer Use Only:</b> This section must be completed
                  by your Paid Preparer only. The preparer must give you a copy
                  of the form in addition to the copy to be transmitted to the
                  IRS.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 5,
                    lineHeight: 1.5,
                  }}
                >
                  If you are a paid preparer, enter your preparer tax
                  identification number (PTIN) in the space provided. Include
                  your complete address. If you work for a firm, you must also
                  enter the firm&apos;s name and the EIN of the firm.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 2290?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  HVUT 2290 form irs can be filed with the IRS electronically or
                  by paper filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Electronic filing or E-filing
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  E-filing your form 2290 heavy use tax with the IRS is the best
                  approach as the process is quick and faster. The IRS also
                  recommends that taxpayers e-file their 2290 returns because
                  they are processed faster and the stamped Schedule 1 will be
                  provided in a matter of minutes.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  You can e-file HVUT Form directly with the IRS or can opt to
                  use an e-file service provider.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    lineHeight: 1.5,
                  }}
                >
                  TaxZerone is an IRS-authorized e-file service provider that
                  supports the electronic filing of Form 2290. You can enter the
                  required information, calculate and pay the balance tax due,
                  transmit the return to the IRS, and get your stamped schedule
                  1 2290 tax form in minutes!
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Another method to file Form 2290 is paper filing. You can
                  manually fill out the form and send it via postal email.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  If you are paper filing your HVUT 2290 form, make sure to send
                  the return earlier before the deadline, as it may take some
                  time for the return to reach the IRS.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 5,
                    lineHeight: 1.5,
                  }}
                >
                  Although two filing options are available for heavy vehicle
                  users, the IRS recommends e-filing as it is simple, easy, and
                  takes less time and cost compared to paper filing.
                </Typography>
                {/* <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      E-file HVUT Form 2290 with TaxZerone.
                    </Typography>
                    <Typography variant="body2">
                      Save your time, money, and effort by e-filing IRS Form
                      2290 with TaxZerone, an IRS-authorized e-file service
                      provider.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
                    >
                      E-File Your 2290 Now
                    </Button>
                  </Box>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 2290 - Mailing address
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  After filling out all the required information, you are
                  required to manually send the return to the IRS by postal
                  mail.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Send a paper copy of Form 2290 to the mailing address
                  mentioned below:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If you are filing a paper return{' '}
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mailing Address
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            With full payment and that payment is not drawn from
                            an international financial institution.
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            P.O. Box 932500
                            <br />
                            Louisville, KY 40293-2500
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Without payment due or if payment is made through
                            EFTPS or by credit/debit card.
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0031
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            With a check or money order drawn from an
                            international financial institution.{' '}
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            International Accounts
                            <br />
                            1973 Rulon White Blvd.
                            <br />
                            Ogden, UT 84201-0038
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 2290?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    lineHeight: 1.5,
                  }}
                >
                  E-filing{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 2290
                  </Link>{' '}
                  can be done in 3 simple steps. Before you start with the
                  e-file process, keep the information below ready.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Information required:
                </Typography>
                <List>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Business Name & Address.</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      EIN (Employer Identification Number).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      VIN (Vehicle Identification Number).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Taxable Gross Weight of Vehicle.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      First Used Month (FUM) of Vehicle.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Once you have all the information above, you can follow the
                  steps below to{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 2290
                  </Link>{' '}
                  using TaxZerone.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 1:</b> Enter the required information, such as your
                  business name, EIN, VIN, taxable gross weight, and first used
                  month (FUM).
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 2:</b> Review the information you provided in the form
                  and pay the balance tax due using the EFW or EFTPS or
                  Credit/Debit card option.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 5,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 3:</b> Transmit the return to the IRS and get your
                  stamped Schedule 1 as soon as you complete the filing process.
                </Typography>
                {/* <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        E-file Form 2290 with TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Quickly e-file HVUT Form 2290 with TaxZerone and get
                        your Stamped Schedule 1 in minutes!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to pay the balance tax due while filing 2290 tax form
                  online?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  While completing step 2, if you have any balance tax due, you
                  can choose to pay the balance due amount using any one of the
                  following methods.
                </Typography>

                <List>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Link
                        href="/resources/electronic-funds-withdrawal-efw/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Electronic Funds Withdrawal
                      </Link>{' '}
                      (EFW)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                        {' '}
                        Electronic Federal Tax Payment System
                      </Link>{' '}
                      (EFTPS)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Credit/Debit card</ListItemText>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file HVUT Form 2290 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File IRS Form 2290 easily with TaxZerone starting at just&nbsp;
              <b>$19.99/return</b>. You will only have to enter the required
              information, calculate and pay the balance tax due, transmit the
              return to the IRS, and get your stamped Schedule 1 in minutes!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              The whole filing process gets completed in less than 5 minutes!
              You will also get email updates on your return status as soon as
              the IRS processes it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file HVUT Form 2290 online?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
              >
                E-file Form 2290 Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get your stamped Schedule 1 in minutes!
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container> */}
      <Container maxWidth="lg" sx={{ mb: 8, mt: { xs: 6, sm: 6, md: 6 } }}>
        <Grid
          container
          spacing={2}
          m={0}
          mt={0}
          className="MuiGrid-root-width"
          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Grid
            size={{ md: 6, xs: 12 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'center',
                xs: 'center',
              },
            }}
          >
            <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  // textAlign: 'center',
                }}
              >
                File IRS Form 2290 for multiple vehicles easily with
                TaxZerone&apos;s bulk upload feature
              </Typography>
              <Typography component="div" variant="body1" sx={{ mb: 3 }}>
                TaxZerone&apos;s bulk upload feature to easily file IRS Form
                2290 for Multiple Vehicles and streamline your tax filing
                process
              </Typography>

              <Box
                sx={{
                  width: 'auto',
                  textAlign: 'left',
                  alignItems: 'center',
                  display: 'inline-block',
                  mt: { xs: 2, sm: 2 },
                }}
              >
                <Button
                  variant="contained"
                  className="btn-primary medium"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290instructions`}
                  sx={{
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  E-file Form 2290
                </Button>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: 0, textAlign: 'center' }}
                  id="auto-magic-2290"
                >
                  Save time & Try it today!
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} textAlign="right">
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box
                component="iframe"
                title="What is Form 2290: A Comprehensive Guide"
                src="https://www.youtube.com/embed/SHQY0wyOAoE?&rel=0"
                sx={{
                  width: { xs: '100%', sm: 500 },
                  height: { xs: '100%', sm: 281 },
                  border: '10px solid #333 !important',
                  borderRadius: 5,
                }}
                allowFullScreen={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form2290Instructions;
